import { REPORT_STATUS } from '@/constants/report';
import type { ReportRecord } from '@/types/report';
import type { UserRecord } from '@/types/user';

export type TeamSubmitFilter = 'all' | 'submitted' | 'not_submitted';

export interface SubordinateReportRow {
  user: UserRecord;
  report: ReportRecord | null;
  submitted: boolean;
}

export function isReportSubmitted(report: ReportRecord | null | undefined): boolean {
  return Boolean(report && report.status !== REPORT_STATUS.DRAFT);
}

export function buildSubordinateReportRows(
  subordinates: UserRecord[],
  reports: ReportRecord[],
): SubordinateReportRow[] {
  const reportByUserId = new Map<string, ReportRecord>();

  for (const report of reports) {
    const existing = reportByUserId.get(report.userId);
    if (!existing || new Date(report.updatedAt) > new Date(existing.updatedAt)) {
      reportByUserId.set(report.userId, report);
    }
  }

  return subordinates.map((user) => {
    const report = reportByUserId.get(user.id) ?? null;
    return {
      user,
      report: isReportSubmitted(report) ? report : null,
      submitted: isReportSubmitted(report),
    };
  });
}

export function filterSubordinateRows(
  rows: SubordinateReportRow[],
  options: {
    submitFilter?: TeamSubmitFilter;
    userId?: string;
  },
): SubordinateReportRow[] {
  let result = rows;

  if (options.userId) {
    result = result.filter((row) => row.user.id === options.userId);
  }

  if (options.submitFilter === 'submitted') {
    result = result.filter((row) => row.submitted);
  } else if (options.submitFilter === 'not_submitted') {
    result = result.filter((row) => !row.submitted);
  }

  return result;
}

export function countTeamSubmission(rows: SubordinateReportRow[]): {
  submitted: number;
  notSubmitted: number;
  total: number;
  submitRate: number;
} {
  const submitted = rows.filter((row) => row.submitted).length;
  const total = rows.length;
  return {
    submitted,
    notSubmitted: total - submitted,
    total,
    submitRate: total > 0 ? Math.round((submitted / total) * 100) : 0,
  };
}

export interface DepartmentSubmissionStat {
  departmentId: string;
  departmentName: string;
  submitted: number;
  total: number;
  submitRate: number;
}

export function buildDepartmentSubmissionStats(
  rows: SubordinateReportRow[],
  departmentNameById: Map<string, string>,
): DepartmentSubmissionStat[] {
  const grouped = new Map<string, { submitted: number; total: number }>();

  for (const row of rows) {
    const departmentId = row.user.departmentId;
    const current = grouped.get(departmentId) ?? { submitted: 0, total: 0 };
    current.total += 1;
    if (row.submitted) {
      current.submitted += 1;
    }
    grouped.set(departmentId, current);
  }

  return [...grouped.entries()]
    .map(([departmentId, counts]) => ({
      departmentId,
      departmentName: departmentNameById.get(departmentId) ?? '未知部门',
      submitted: counts.submitted,
      total: counts.total,
      submitRate: counts.total > 0 ? Math.round((counts.submitted / counts.total) * 100) : 0,
    }))
    .sort((a, b) => b.total - a.total || a.departmentName.localeCompare(b.departmentName, 'zh-CN'));
}
